import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import getTasks from '../graphql/query/getTasks';

export interface UpcomingTask {
  _id: string;
  docNo: string;
  title: string;
  name: string;
  nameAr: string;
  customerName: string;
  customerNameAr: string;
  start: string;
  daysUntilStart: number;
  projectName?: string;
  projectNameAr?: string;
}

export const useUpcomingTasksNotification = (isRTL: boolean) => {
  const [upcomingTasks, setUpcomingTasks] = useState<UpcomingTask[]>([]);
  const [getTasksQuery, { data }] = useLazyQuery(getTasks, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    // Check for upcoming tasks on component mount
    getTasksQuery({ variables: { isRTL } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRTL]);

  useEffect(() => {
    if (data?.getTasks?.data) {
      const tasks = data.getTasks.data;
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day

      const sevenDaysFromNow = new Date(today);
      sevenDaysFromNow.setDate(today.getDate() + 7);
      sevenDaysFromNow.setHours(23, 59, 59, 999); // Set to end of 7th day

      const upcoming: UpcomingTask[] = tasks
        .filter((task: any) => {
          if (!task.start || task.disabled || task.isClosed) return false;

          const startDate = new Date(task.start);
          return startDate >= today && startDate <= sevenDaysFromNow;
        })
        .map((task: any) => {
          const startDate = new Date(task.start);
          const daysUntilStart = Math.ceil(
            (startDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
          );

          return {
            _id: task._id,
            docNo: task.docNo,
            title: task.title,
            name: task.name,
            nameAr: task.nameAr,
            customerName: task.customerName,
            customerNameAr: task.customerNameAr,
            projectName: task.projectName,
            projectNameAr: task.projectNameAr,
            start: task.start,
            daysUntilStart,
          };
        })
        .sort(
          (a: UpcomingTask, b: UpcomingTask) =>
            a.daysUntilStart - b.daysUntilStart,
        );

      setUpcomingTasks(upcoming);

      // Show browser notifications for upcoming tasks
      if (upcoming.length > 0 && 'Notification' in window) {
        // Request permission if not granted
        if (Notification.permission === 'default') {
          Notification.requestPermission().then((permission) => {
            if (permission === 'granted') {
              showNotifications(upcoming, isRTL);
            }
          });
        } else if (Notification.permission === 'granted') {
          showNotifications(upcoming, isRTL);
        }
      }
    }
  }, [data, isRTL]);

  const showNotifications = (tasks: UpcomingTask[], isRTL: boolean) => {
    tasks.forEach((task) => {
      const customerName = isRTL ? task.customerNameAr : task.customerName;
      const taskName = isRTL
        ? task.nameAr || task.title
        : task.name || task.title;

      let title = '';
      let body = '';

      if (task.daysUntilStart === 0) {
        title = isRTL ? 'مهمة تبدأ اليوم!' : 'Task Starts Today!';
        body = isRTL
          ? `المهمة ${task.docNo} - ${taskName} للعميل ${customerName} تبدأ اليوم`
          : `Task ${task.docNo} - ${taskName} for customer ${customerName} starts today`;
      } else if (task.daysUntilStart === 1) {
        title = isRTL ? 'مهمة تبدأ غداً!' : 'Task Starts Tomorrow!';
        body = isRTL
          ? `المهمة ${task.docNo} - ${taskName} للعميل ${customerName} تبدأ غداً`
          : `Task ${task.docNo} - ${taskName} for customer ${customerName} starts tomorrow`;
      } else {
        title = isRTL
          ? `مهمة تبدأ خلال ${task.daysUntilStart} أيام`
          : `Task Starts in ${task.daysUntilStart} days`;
        body = isRTL
          ? `المهمة ${task.docNo} - ${taskName} للعميل ${customerName} تبدأ خلال ${task.daysUntilStart} أيام`
          : `Task ${task.docNo} - ${taskName} for customer ${customerName} starts in ${task.daysUntilStart} days`;
      }

      new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: task._id, // Prevent duplicate notifications
      });
    });
  };

  const refreshTasks = () => {
    getTasksQuery({ variables: { isRTL } });
  };

  return {
    upcomingTasks,
    refreshTasks,
    hasUpcomingTasks: upcomingTasks.length > 0,
  };
};
